import { apiClient } from "@apis/apiClient";
import type {
  UserInfo,
  HelpdeskTokenParams,
  HelpdeskTokenResponse,
  LoginResponse,
  ExternalIdPLoginParams,
  OAuth2AuthorizeParams,
  OAuth2AuthorizeResponse,
  ApiResponse,
} from "./authenticationClient.dto";

/**
 * Authentication API Client
 *
 * Provides methods for user authentication, login, logout, and OAuth2 operations.
 * Uses DTOs from authenticationClient.dto.ts for type safety.
 */
const authenticationClient = {
  /**
   * Get current user information
   * @returns User information
   */
  getUserInfo(): Promise<ApiResponse<UserInfo>> {
    return apiClient.get(`/rest/authentication/getUserInfo`);
  },

  /**
   * Get helpdesk token for support access
   * @param params - Helpdesk token parameters
   * @returns Helpdesk token
   */
  getHelpdeskToken(
    params: HelpdeskTokenParams
  ): Promise<ApiResponse<HelpdeskTokenResponse>> {
    return apiClient.post(
      `/rest/authentication/helpdeskToken`,
      JSON.stringify(params),
      {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      }
    );
  },

  /**
   * Login with username and password
   * @param username - User's username
   * @param password - User's password
   * @returns Login response
   */
  login(
    username: string,
    password: string
  ): Promise<ApiResponse<LoginResponse>> {
    return apiClient.post(`/rest/authentication/login/${username}`, password, {
      headers: { "content-type": "text/plain" },
    });
  },

  /**
   * Logout current user
   * @returns Logout response
   */
  logout(): Promise<ApiResponse<void>> {
    return apiClient.get(`/rest/authentication/logout`, {
      headers: { "content-type": "text/plain" },
    });
  },

  /**
   * Login with external identity provider
   * @param orgId - Organization ID
   * @param params - External IdP login parameters
   * @returns Login response
   */
  loginExternalIdP(
    orgId: number,
    params: ExternalIdPLoginParams
  ): Promise<ApiResponse<LoginResponse>> {
    return apiClient.get(`/rest/authentication/login/externalIdP/${orgId}`, {
      headers: { "content-type": "text/plain" },
      params: params,
    });
  },

  /**
   * OAuth2 authorization
   * @param params - OAuth2 authorize parameters
   * @returns OAuth2 authorization response
   */
  oauth2Authorize(
    params: OAuth2AuthorizeParams
  ): Promise<ApiResponse<OAuth2AuthorizeResponse>> {
    return apiClient.post(`/rest/oauth2/authorize`, params, {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    });
  },
};

export default authenticationClient;
